/**
 * The suggested next stages — one table, for the whole frontend.
 *
 * There used to be three copies of this: the backend's `DEMAND_TRANSITIONS`,
 * `DemandKanban`'s `VALID_TRANSITIONS` (drop-zone tint) and
 * `ChangeStageDialog`'s `NATURAL_NEXT` (whether a written reason is required).
 * All three once claimed "Resourcing is reachable ONLY via Triaging", none of
 * them enforced it, and #1049 corrected the claim without collapsing the
 * copies. What was left was two frontend tables that disagreed.
 *
 * **They disagreed only on their legacy rows, and those rows were invented
 * here.** `DEMAND_TRANSITIONS` has six keys — the active stages — and no
 * legacy keys at all. The frontend copies had eleven, and the five extra ones
 * drifted apart from each other: `staffed` suggested
 * `['not_prioritized','resolved']` in one and `[]` in the other,
 * `under_review` and `open_to_application` differed by `resolved`.
 *
 * So the legacy rows are gone rather than reconciled. A demand stored on a
 * legacy value reads as its canonical everywhere else in the app, and now here
 * too — `suggestedNext('under_review')` is `suggestedNext('scoping')`, derived,
 * not transcribed. There is nothing left to keep in step.
 *
 * NOT A GATE. Every target the picker offers is reachable; an off-path one
 * simply asks for a written reason, which lands in the audit trail. Reading
 * this as an allow-list is the most common wrong assumption about this
 * codebase — see CLAUDE.md.
 */

import type { DemandStage } from '../types/demand'

/**
 * Legacy stage → the active stage it reads as. Mirrors `STAGE_ALIASES` in
 * `backend-python/models/demand.py`.
 *
 * Legacy values are read-tolerated and never produced: the v2.8.38 cutover
 * remapped every row in `demands`, `demand_stage_history` keeps the old
 * vocabulary on purpose, and since #1268 the transition endpoint refuses any
 * of these as a target. They arrive here only as the *current* stage of a row
 * that predates all of that.
 */
const CANONICAL_OF: Partial<Record<DemandStage, DemandStage>> = {
  under_review: 'scoping',
  open_to_application: 'resourcing',
  staffed: 'resourcing',
  not_prioritized: 'parking',
  resolved: 'closed',
}

/** The active stage a (possibly legacy) stage reads as. */
export function canonicalStage(stage: DemandStage): DemandStage {
  return CANONICAL_OF[stage] ?? stage
}

/**
 * Keyed on the active stages only. Matches `DEMAND_TRANSITIONS` key for key —
 * deliberately, so the two can be read side by side.
 */
const SUGGESTED_NEXT: Record<string, DemandStage[]> = {
  new: ['scoping', 'parking'],
  scoping: ['triaging', 'parking', 'closed'],
  triaging: ['scoping', 'resourcing', 'parking', 'closed'],
  resourcing: ['triaging', 'parking', 'closed'],
  parking: ['scoping', 'triaging', 'closed'],
  closed: [],
}

/** The suggested next stages from `from`, legacy values included via their canonical. */
export function suggestedNext(from: DemandStage): DemandStage[] {
  return SUGGESTED_NEXT[canonicalStage(from)] ?? []
}

/**
 * Is `to` the suggested next step from `from`?
 *
 * A move to the stage the demand is already in is never suggested — it is not
 * off-path, it is a no-op, and the API answers 400 "Already in stage".
 */
export function isSuggestedNext(from: DemandStage, to: DemandStage): boolean {
  if (from === to) return false
  return suggestedNext(from).includes(to)
}
